import { createFileRoute, redirect } from '@tanstack/react-router'
import { Flex, Typography } from 'antd'
import React from 'react'
import { PortaledBreadcrumb } from '../components/portaled-breadcrumb'
import { useCampaigns } from '../campaign/hooks'
import { NetworkErrors } from '../components/network-errors'
import { CampaignList } from '../campaign/components/campaign-list'
import { useTranslation } from 'react-i18next'
import { useTitle } from '../hooks/use-title'
import { CampaignCreateButton } from '../campaign/components/campaign-create-button'
import { useAuth } from '../auth'
import { INDEX_CAMPAIGN, STORE_CAMPAIGN } from '../user/permission'
import { campaignQuery } from '../campaign/campaign'
import { CampaignSearch } from '../campaign/components/campaign-search'
import { CampaignSort, CampaignSortValue, defaultCampaignSort } from '../campaign/components/campaign-sort'
import { PageTitle } from '../components/page-title'

export const Route = createFileRoute('/_auth/_main/campaigns/')({
    beforeLoad: ({ context }) => {
        if (!context.auth.can(INDEX_CAMPAIGN)) {
            throw redirect({
                to: '/dashboard',
            })
        }
    },
    component: CampaignsIndex,
    validateSearch: campaignQuery,
})

function CampaignsIndex() {
    const { t } = useTranslation()
    const { can } = useAuth()
    const search = Route.useSearch()
    const navigate = Route.useNavigate()
    useTitle(t('Campaigns'))

    const sort = search.sort ?? defaultCampaignSort
    const titleFilter = search.filters?.title?.[0] ?? ''
    const isSearching = titleFilter.length > 0

    const query = useCampaigns({
        query: {
            page: search.page,
            sort,
            filters: search.filters,
        },
    })

    const setSort = (value: CampaignSortValue) => {
        navigate({ search: (prev) => ({ ...prev, page: 1, sort: value }) })
    }

    const setTitle = (value: string) => {
        navigate({
            search: (prev) => ({ ...prev, page: 1, filters: value ? { title: [value] } : undefined }),
        })
    }

    const setPage = (page: number) => {
        navigate({ search: (prev) => ({ ...prev, page }) })
    }

    return (
        <>
            <PortaledBreadcrumb
                items={[
                    {
                        title: t('Campaigns'),
                    },
                ]}
            />
            {query.isPending || query.isError ? (
                <NetworkErrors query={query} />
            ) : query.data.data.length === 0 && !isSearching ? (
                <HasNoCampaigns />
            ) : (
                <>
                    <PageTitle
                        title={t('Campaigns')}
                        count={query.data.meta.total}
                        extra={
                            <Flex
                                gap="small"
                                align="center"
                                wrap="wrap"
                                className="w-full sm:w-auto"
                            >
                                <CampaignSearch
                                    value={titleFilter}
                                    onChange={setTitle}
                                />
                                <CampaignSort
                                    value={sort}
                                    onChange={setSort}
                                />
                                {can(STORE_CAMPAIGN) && <CampaignCreateButton />}
                            </Flex>
                        }
                    />
                    <CampaignList
                        campaigns={query.data.data}
                        pagination={{
                            total: query.data.meta.total,
                            current: query.data.meta.currentPage,
                            onChange: setPage,
                        }}
                    />
                </>
            )}
        </>
    )
}

function HasNoCampaigns() {
    const { t } = useTranslation()

    return (
        <Flex
            vertical
            align="center"
            gap="middle"
            className="px-6 py-16 text-center"
        >
            <Typography.Title
                level={4}
                className="mb-0"
            >
                {t("You don't have a campaign yet")}
            </Typography.Title>
            <Typography.Paragraph
                type="secondary"
                className="mb-0 max-w-md"
            >
                {t(
                    'Get started by creating a campaign, a campaign will instruct Evaa to start conversations with candidates'
                )}
            </Typography.Paragraph>
            <CampaignCreateButton />
        </Flex>
    )
}
